import type { FC } from "react";
import type { LoaderFunction, MetaFunction } from "react-router";
import { data as responseData } from "react-router";
import type { ObjectSchema } from "yup";
import { object, string } from "yup";

import { prepareSeoData } from "~/core/transformers/seo.transformer";
import { getOriginalUrl } from "~/shared/utils/get-original-url";
import { PageConstructor } from "~/widgets/page-constructor";
import { loadPageConstructor } from "~/widgets/page-constructor/page-constructor.server";

import type { Route } from "./+types/$lang.$id";

const createDetailedTournamentValidator = (): ObjectSchema<{
  id: string;
  lang: string;
}> => {
  return object({
    id: string().required(),
    lang: string().required(),
  });
};

export const loader = (async ({
  params,
  context: { sdk, settings },
  request,
}) => {
  const { id, lang } = createDetailedTournamentValidator().validateSync(params);
  const pageConstructorData = await loadPageConstructor({
    sdk,
    slug: id,
    segment: null,
    lang,
  });

  return responseData(
    {
      pageConstructorData,
      originalUrl: getOriginalUrl(request.url),
      languages: settings?.languages,
    },
    {
      headers: {
        "Cache-Control": "public, max-age=300",
      },
    },
  );
}) satisfies LoaderFunction;

export function headers({
  loaderHeaders,
  actionHeaders,
}: Route.HeadersArgs): Headers {
  return loaderHeaders ?? actionHeaders;
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  return prepareSeoData({
    pageSeo: data?.pageConstructorData?.page.seo,
    fallbackSeo: data?.pageConstructorData?.fallbackSeo?.seo,
    originalUrl: `${data?.originalUrl}`,
    currentLang: params?.lang ?? "",
    languages: data?.languages ?? [],
  });
};

const Page: FC<Route.ComponentProps> = ({
  loaderData: { pageConstructorData },
}) => {
  return <PageConstructor data={pageConstructorData} />;
};

export default Page;
